// Identity, organizations, notifications, uploads, contact and the stubs for
// screens that are not routed (processing, settings, dashboard).

import type { DashboardStatistics } from '../../types/dashboard.type';
import type { ProcessingBatchSchema } from '../../types/processing.type';
import { ProcessingPriority, ProcessingStatus, ProcessingType } from '../../types/processing.type';
import {
  DEMO_OUTLET_ORG_ID,
  DEMO_TRUCKS,
  DEMO_USER_ID,
  buildFakeJwt,
  daysAgo,
  demoError,
  ids,
  isFakeJwt,
  iso,
  monthStart,
  paginate,
  parseQueryParam,
  type DemoRouteDef,
} from '../core';
import { ensureDemoData } from '../data';
import { materialName, wasteTypeOf } from '../data/catalog';
import { bodyOf, nowIso, round1, round2 } from '../data/common';
import { truckRecordById } from '../data/fleet';
import { addNotification, listNotifications, markNotificationRead } from '../data/notifications';
import { allOrders } from '../data/orders';
import {
  createOrganization,
  currentUser,
  listOrganizations,
  organizationById,
  removeUser,
  updateOrganization,
  updateUser,
  usersFor,
} from '../data/people';
import { demoUploadUrl, newUploadId } from '../data/uploads';

const NOT_IN_DEMO = 'Not available in demo';

function sessionToken(key: string): string {
  try {
    const value = globalThis.sessionStorage?.getItem(key);
    if (value && isFakeJwt(value)) return value;
  } catch {
    // fall through
  }
  return buildFakeJwt();
}

function notFound(what: string) {
  return demoError(404, `${what} not found`);
}

// ── Processing batches (the processing screens use local data; the service still answers) ──

const BATCHES: ProcessingBatchSchema[] = Array.from({ length: 8 }, (_, i) => {
  const statuses = [ProcessingStatus.COMPLETED, ProcessingStatus.IN_PROGRESS, ProcessingStatus.QUALITY_CHECK, ProcessingStatus.PENDING];
  const types = [ProcessingType.SORTING, ProcessingType.COMPACTING, ProcessingType.SHREDDING, ProcessingType.CLEANING];
  return {
    id: ids.hseRecord(0x700 + i),
    batch_number: `SUL-B-${String(2401 + i)}`,
    status: statuses[i % statuses.length],
    type: types[i % types.length],
    priority: i % 3 === 0 ? ProcessingPriority.HIGH : ProcessingPriority.MEDIUM,
    material_type: ['cardboard', 'plastic', 'paper', 'metal'][i % 4],
    quantity: 4200 + i * 650,
    unit: 'kg',
    start_date: iso(daysAgo(10 - i)),
    end_date: i % 4 === 0 ? iso(daysAgo(9 - i)) : undefined,
    created_at: iso(daysAgo(11 - i)),
    updated_at: iso(daysAgo(Math.max(0, 9 - i))),
    quality_score: 82 + (i % 5) * 3,
    efficiency_score: 78 + (i % 4) * 4,
    assigned_to: ids.manager(1),
    assigned_to_name: 'Tariq Al-Rashid',
  };
});

let batches: ProcessingBatchSchema[] = BATCHES.map((b) => ({ ...b }));
let contactCount = 0;

// ── Dashboard statistics (outlet dashboard; the page is not routed in ops) ──

function dashboardStatistics(orgId: string): DashboardStatistics {
  const start = monthStart(0).toISOString();
  const prevStart = monthStart(-1).toISOString();
  const mine = allOrders().filter((o) => o.organization_id === orgId);
  const inMonth = (from: string, to: string) => mine.filter((o) => o.created_at >= from && o.created_at < to);
  const thisMonth = inMonth(start, '9999');
  const lastMonth = inMonth(prevStart, start);
  const kg = (list: typeof mine) => round1(list.filter((o) => o.status === 'completed').reduce((s, o) => s + (o.verified_kg ?? 0), 0));
  const revenue = (list: typeof mine) => round2(list.reduce((s, o) => s + (o.sale_amount ?? 0), 0));
  const breakdown = (list: typeof mine) => {
    const m = new Map<string, number>();
    list.filter((o) => o.status === 'completed').forEach((o) => m.set(wasteTypeOf(o.material_id), (m.get(wasteTypeOf(o.material_id)) ?? 0) + (o.verified_kg ?? 0)));
    return Array.from(m.entries()).map(([waste_type, amount]) => ({ waste_type, amount: round1(amount) }));
  };
  const daily = (list: typeof mine) =>
    list.filter((o) => o.status === 'completed').map((o) => ({ date: (o.completed_at ?? o.created_at).slice(0, 10), waste_type: materialName(o.material_id), amount: o.verified_kg ?? 0 }));
  const trucks = DEMO_TRUCKS.slice(0, 4).map((t) => {
    const truck = truckRecordById(t.id);
    return {
      truck_id: t.id,
      truck_name: truck?.name ?? t.plate_number,
      latitude: truck?.latitude ?? 24.7,
      longitude: truck?.longitude ?? 46.7,
      last_updated: nowIso(),
      status: (t.status === 'maintenance' ? 'maintenance' : t.status === 'available' ? 'idle' : 'active') as 'active' | 'idle' | 'maintenance',
      current_load: Math.round(t.capacity_kg * 0.4),
      capacity: t.capacity_kg,
    };
  });
  const both = {
    time_from_full_to_collection: 18.5,
    time_from_collection_to_delivery: 2.4,
    full_percentage: 34,
    recycler_appetite: 27500,
    truck_driver_count: 7,
    sustainable_supply: kg(thisMonth),
    collection_frequency: thisMonth.length,
    average_container_fill_rate: 61,
    waste_diversion_rate: 71,
    average_incentive_per_kg: 0.62,
    trips_today_count: 4,
    total_revenue_last_month: revenue(lastMonth),
    average_processing_time: 26,
    quality_compliance_rate: 95,
    rejected_waste_percentage: 4.5,
    processing_capacity_utilization: 68,
    average_route_efficiency: 87,
    fuel_consumption_per_trip: 18.4,
    maintenance_schedule_compliance: 92,
    driver_performance_score: 88,
    customer_satisfaction_score: 4.6,
    waste_compaction_efficiency: 74,
    storage_capacity_utilization: 58,
    emergency_collection_requests: 2,
    waste_quality_rating: 4.3,
  };
  return {
    ...both,
    supply_per_recycler: [{ recycler_name: 'Riyadh Green Recycling Co.', supply_amount: kg(thisMonth) }],
    incoming_waste_breakdown: daily(thisMonth),
    waste_type_breakdown: breakdown(thisMonth),
    active_trucks: trucks,
    previous_month_time_from_full_to_collection: 21.2,
    previous_month_time_from_collection_to_delivery: 2.7,
    previous_month_full_percentage: 38,
    previous_month_recycler_appetite: 26000,
    previous_month_truck_driver_count: 7,
    previous_month_supply_per_recycler: [{ recycler_name: 'Riyadh Green Recycling Co.', supply_amount: kg(lastMonth) }],
    previous_month_sustainable_supply: kg(lastMonth),
    previous_month_collection_frequency: lastMonth.length,
    previous_month_average_container_fill_rate: 64,
    previous_month_waste_diversion_rate: 68,
    previous_month_average_incentive_per_kg: 0.6,
    previous_month_trips_today_count: 3,
    previous_month_incoming_waste_breakdown: daily(lastMonth),
    previous_month_waste_type_breakdown: breakdown(lastMonth),
    previous_month_total_revenue_last_month: revenue(inMonth(monthStart(-2).toISOString(), prevStart)),
    previous_month_average_processing_time: 29,
    previous_month_quality_compliance_rate: 93,
    previous_month_rejected_waste_percentage: 5.1,
    previous_month_processing_capacity_utilization: 64,
    previous_month_average_route_efficiency: 84,
    previous_month_fuel_consumption_per_trip: 19.1,
    previous_month_maintenance_schedule_compliance: 90,
    previous_month_driver_performance_score: 86,
    previous_month_customer_satisfaction_score: 4.5,
    previous_month_waste_compaction_efficiency: 71,
    previous_month_storage_capacity_utilization: 61,
    previous_month_emergency_collection_requests: 3,
    previous_month_waste_quality_rating: 4.2,
  };
}

const userPreferences = {
  id: ids.user(1),
  user_id: DEMO_USER_ID,
  theme: 'system' as const,
  language: 'en',
  timezone: 'Asia/Riyadh',
  date_format: 'dd/MM/yyyy',
  notifications: { email: true, push: true, sms: false, order_updates: true, inventory_alerts: true, system_updates: false },
  created_at: iso(daysAgo(300)),
  updated_at: iso(daysAgo(4)),
};

export const platformRoutes: DemoRouteDef[] = [
  // ── Auth ──
  ['post', '/user/login', () => demoError(400, NOT_IN_DEMO)],
  ['post', '/user/register', () => demoError(400, NOT_IN_DEMO)],
  ['post', '/user/forgot-password', () => demoError(400, NOT_IN_DEMO)],
  ['post', '/user/reset-password', () => demoError(400, NOT_IN_DEMO)],
  [
    'post',
    '/app_user/refresh_token',
    () => ({ result: { access_token: sessionToken('access_token'), refresh_token: sessionToken('refresh_token'), token_type: 'bearer' } }),
  ],

  // ── Users ──
  ['get', '/user/me', () => {
    ensureDemoData();
    return { result: currentUser() };
  }],
  ['get', '/user/:orgId', ({ params }) => {
    ensureDemoData();
    return { result: usersFor(params.orgId) };
  }],
  ['put', '/user/:orgId/:userId', ({ params, body }) => {
    ensureDemoData();
    const user = updateUser(params.orgId, params.userId, bodyOf(body));
    return user ? { result: user } : notFound('User');
  }],
  ['delete', '/user/:userId', ({ params }) => {
    ensureDemoData();
    if (params.userId === DEMO_USER_ID) return demoError(403, 'The demo user cannot be deleted');
    return removeUser(params.userId) ? { result: { success: true } } : notFound('User');
  }],

  // ── Organizations ──
  ['get', '/organization', ({ query }) => {
    ensureDemoData();
    const name = parseQueryParam({ query }, 'name')?.toLowerCase();
    const type = parseQueryParam({ query }, 'organization_type');
    const rows = listOrganizations()
      .filter((o) => !name || o.name.toLowerCase().includes(name) || String(o.name_ar ?? '').includes(name))
      .filter((o) => !type || o.organization_type === type);
    const page = paginate(rows, query.page, query.limit);
    return { result: { organizations: page.items, total: page.total, page: page.page, limit: page.limit } };
  }],
  ['post', '/organization/onboarding', () => ({
    result: { submission_id: `DEMO-${Date.now().toString(36).toUpperCase()}`, status: 'received' },
  })],
  ['post', '/organization', ({ body }) => {
    ensureDemoData();
    return { result: createOrganization(bodyOf(body)) };
  }],
  ['get', '/organization/:orgId', ({ params }) => {
    ensureDemoData();
    const org = organizationById(params.orgId);
    return org ? { result: org } : notFound('Organization');
  }],
  ['put', '/organization/:orgId', ({ params, body }) => {
    ensureDemoData();
    const org = updateOrganization(params.orgId, bodyOf(body));
    return org ? { result: org } : notFound('Organization');
  }],
  ['delete', '/organization/:orgId', () => demoError(403, 'Demo organizations cannot be deleted')],

  // ── Notifications ──
  ['post', '/notification/register-player', ({ body }) => {
    const b = bodyOf(body);
    return { result: { success: true, message: 'Registered (demo)', player_id: String(b.player_id ?? 'demo-player') } };
  }],
  ['post', '/notification/unregister-player/:playerId', ({ params }) => ({
    result: { success: true, message: 'Unregistered (demo)', player_id: params.playerId },
  })],
  ['post', '/notification/users', ({ body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    const n = addNotification(String(b.organization_id ?? DEMO_OUTLET_ORG_ID), [String(b.title_en ?? ''), String(b.title_ar ?? '')], [String(b.message_en ?? ''), String(b.message_ar ?? '')], (b.data as Record<string, unknown>) ?? {});
    return { result: [n] };
  }],
  ['post', '/notification', ({ body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    return {
      result: addNotification(String(b.organization_id ?? DEMO_OUTLET_ORG_ID), [String(b.title_en ?? ''), String(b.title_ar ?? '')], [String(b.message_en ?? ''), String(b.message_ar ?? '')], (b.data as Record<string, unknown>) ?? {}),
    };
  }],
  ['get', '/notification/:orgId', ({ params, query }) => {
    ensureDemoData();
    let rows = listNotifications(params.orgId);
    const before = parseQueryParam({ query }, 'before_message_id');
    const after = parseQueryParam({ query }, 'after_message_id');
    if (before) {
      const idx = rows.findIndex((n) => n.id === before);
      if (idx >= 0) rows = rows.slice(idx + 1);
    }
    if (after) {
      const idx = rows.findIndex((n) => n.id === after);
      if (idx >= 0) rows = rows.slice(0, idx);
    }
    const total = listNotifications(params.orgId).length;
    const limit = Number(parseQueryParam({ query }, 'limit') ?? 0);
    return { result: { result_list: limit > 0 ? rows.slice(0, limit) : rows, total_count: total } };
  }],
  ['put', '/notification/:orgId/:notificationId/read', ({ params }) => {
    ensureDemoData();
    const n = markNotificationRead(params.orgId, params.notificationId);
    return n ? { result: n } : notFound('Notification');
  }],

  // ── Contact, uploads ──
  ['post', '/contact', () => {
    contactCount += 1;
    return { result: { id: ids.changeRequest(0x500 + contactCount), created_at: nowIso() } };
  }],
  ['post', '/upload', ({ body, query }) => {
    const id = newUploadId();
    const b = bodyOf(body);
    const ext = parseQueryParam({ query }, 'file_extension') ?? 'bin';
    return { result: { upload_url: demoUploadUrl(id), file_path: `demo/${parseQueryParam({ query }, 'file_type') ?? 'custom'}/${id}.${ext}`, id, original_name: b.original_name ?? null } };
  }],

  // ── Settings (screens not routed in ops) ──
  ['get', '/settings/user-preferences', () => ({ data: [userPreferences], total: 1, page: 1, limit: 1, result: userPreferences })],
  ['put', '/settings/user-preferences/notifications', ({ body }) => {
    Object.assign(userPreferences.notifications, bodyOf(body));
    return { result: userPreferences };
  }],
  ['put', '/settings/user-preferences', ({ body }) => {
    const b = bodyOf(body);
    Object.assign(userPreferences, b, { updated_at: nowIso() });
    return { ...userPreferences, result: userPreferences };
  }],
  ['put', '/settings/organization/security', ({ body }) => ({ result: { ...bodyOf(body), success: true } })],

  // ── Processing batches ──
  ['get', '/processing/batch', ({ query }) => {
    const page = paginate(batches, query.page ?? 1, query.limit ?? 20);
    const totalPages = Math.ceil(page.total / page.limit);
    return { data: page.items, total: page.total, page: page.page, hasMore: page.page < totalPages, totalPages, result: page.items };
  }],
  ['get', '/processing/batch/:batchId', ({ params }) => {
    const batch = batches.find((b) => b.id === params.batchId);
    return batch ? { result: batch, ...batch } : notFound('Batch');
  }],
  ['post', '/processing/batch', ({ body }) => {
    const b = bodyOf(body) as Partial<ProcessingBatchSchema>;
    const batch: ProcessingBatchSchema = {
      id: ids.hseRecord(0x800 + batches.length),
      batch_number: `SUL-B-${2401 + batches.length}`,
      status: ProcessingStatus.PENDING,
      type: b.type ?? ProcessingType.SORTING,
      priority: b.priority ?? ProcessingPriority.MEDIUM,
      material_type: b.material_type ?? 'cardboard',
      quantity: Number(b.quantity ?? 0),
      unit: b.unit ?? 'kg',
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    batches.unshift(batch);
    return { result: batch, ...batch };
  }],
  ['put', '/processing/batch/:batchId', ({ params, body }) => {
    const batch = batches.find((b) => b.id === params.batchId);
    if (!batch) return notFound('Batch');
    Object.assign(batch, bodyOf(body), { updated_at: nowIso() });
    return { result: batch, ...batch };
  }],
  ['delete', '/processing/batch/:batchId', ({ params }) => {
    batches = batches.filter((b) => b.id !== params.batchId);
    return { result: { success: true } };
  }],

  // ── Outlet dashboard statistics ──
  ['get', '/waste-outlet-dashboard-statistics', ({ query }) => {
    ensureDemoData();
    const stats = dashboardStatistics(parseQueryParam({ query }, 'waste_outlet_id') ?? DEMO_OUTLET_ORG_ID);
    return { ...stats, result: stats };
  }],
];

/** Restore the module-level stubs to their seeded state. */
export function resetPlatformStubs(): void {
  batches = BATCHES.map((b) => ({ ...b }));
  contactCount = 0;
}
